import {
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  SingleSelectDropdownProps  } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';
import * as _ from 'underscore';

import { SiteRoleInfoLink } from '~src/userManagement/components/SiteRoleInfoLink';

import {
  CreatorTier,
  ExplorerTier,
  LicensingTier,
  LicensingTierLocPrefix,
  SiteRoleOption,
  SiteRoleOptionsResource,
  UnlicensedTier,
  ViewerTier
  } from '../../../userManagement/services/resources/SiteRoleOptions';
import { SiteRolesResource } from '../../../userManagement/services/resources/SiteRoles';
import { CommonStyles } from '../CommonStyles';
import { TypeUnsafeTranslation } from '../IReactTranslateService';



const SITE_ROLE_PICKER_TEST_ID = 'site-role-picker';
const SITE_ROLE_INFO_TEST_ID = 'site-role-info';

export const DefaultDropdownMenuWidth = 370;
export const SiteRoleInfoContainerStyle: React.CSSProperties = {
  display: 'inline-flex'
};
const TierSections = [CreatorTier, ExplorerTier, ViewerTier, UnlicensedTier];
const SectionDivider = {separator: true};
const TwoSpaces = '\u00A0\u00A0';
const SiteRoleIndentation = TwoSpaces;
const HeaderTestIdSuffix = 'Header';

export interface SiteRolePickerMenuState {
  currentSiteRoleSelection: string;
}

/**
 * A drop down menu that groups site roles based on subscription licensing tiers
 */
export class SiteRolePickerMenu extends React.Component<SiteRolePickerMenu.Props, SiteRolePickerMenuState> {
  private defaultSiteRoleDropdownWidth = DefaultDropdownMenuWidth - CommonStyles.Sizes.DefaultSpace - (2 * CommonStyles.Sizes.TextBoxBorderSize);
  public boundSelectSiteRole: (value: string) => void = value => this.handleSelection(value);

  public constructor(props: SiteRolePickerMenu.Props) {
    super(props);
    this.state = {currentSiteRoleSelection: props.selectedRole.role};
  }

  public componentWillReceiveProps(nextProps: SiteRolePickerMenu.Props) {
    this.setState({currentSiteRoleSelection: nextProps.selectedRole.role});
  }

  protected handleSelection(newOption: string): void {
    this.setState({currentSiteRoleSelection: newOption});
    const selectedRoleOption: SiteRoleOption = _.find(this.props.siteRoleOptions, (option: SiteRoleOption) => {
        return newOption === option.role;
    });
    this.props.onOptionSelected(selectedRoleOption);
  }

  public render(): JSX.Element {
    const dropdownWidth = (this.props.style && this.props.style.width) ? this.props.style.width : this.defaultSiteRoleDropdownWidth;
    const dropdownLabel = this.props.showSiteRoleInfoLink ? this.props.dropdownLabel : undefined;

    return (<div className={style({
      display: 'inline-block'
    })}>
      <SingleSelectDropdown
      className={style({
        width: dropdownWidth,
        ...this.props.style,
      })}
      disabled={this.props.isDisabled}
      value={this.getButtonContent()}
      onChange={this.boundSelectSiteRole}
      testId={SITE_ROLE_PICKER_TEST_ID}
      list={this.getMenuSiteRoleInfo()}
      label={dropdownLabel}
      type={DropdownButtonType.Outline}
    />{this.props.showSiteRoleInfoLink && this.getDropdownLabelWithInfo()}</div>);
  }

  private getDropdownLabelWithInfo(): JSX.Element {
    return <div style={SiteRoleInfoContainerStyle}>
      <SiteRoleInfoLink/>
      {this.props.message}
    </div>;
  }

  protected getMenuSiteRoleInfo(): JSX.Element {
    let siteRoleOptions: ListItem[] = [];
    if (this.props.includeAnySiteRoleOption) {
      siteRoleOptions = siteRoleOptions.concat({
        value: SiteRoleOptionsResource.instance.getAnySiteRole().role,
        content: this.getSiteRoleOptionDisplayValue(SiteRoleOptionsResource.instance.getAnySiteRole()),
        disabled: false
      });
      siteRoleOptions = siteRoleOptions.concat(SectionDivider);
    }
    for (let i = 0; i < TierSections.length; i++) {
      const tier = TierSections[i];
      const siteRoleOptionsForTier = this.getSiteRoleOptionsForTier(tier);
      if (siteRoleOptionsForTier.length > 0 && tier !== UnlicensedTier){
        const tierSectionHeader = this.getHeaderForTierSection(tier);
        siteRoleOptions = siteRoleOptions.concat(tierSectionHeader);
      }
      siteRoleOptions = siteRoleOptions.concat(siteRoleOptionsForTier);
      if (i < TierSections.length-1 && siteRoleOptionsForTier.length > 0) {
        siteRoleOptions = siteRoleOptions.concat(SectionDivider);
      }
    }
    return <List items={siteRoleOptions} />;
  }

  private getHeaderForTierSection(tierRole: LicensingTier): ListItem {
    return {
      value: tierRole + HeaderTestIdSuffix,
      content: TypeUnsafeTranslation.translate(LicensingTierLocPrefix + tierRole),
      disabled: true
    };
  }

  private getSiteRoleOptionsForTier(tierRole: LicensingTier): ListItem[]{
    const siteRoleOptionsForTier = _.filter(this.props.siteRoleOptions, (siteRoleOption: SiteRoleOption) => {
      return siteRoleOption.licenseTierRole === tierRole;
    });
    return siteRoleOptionsForTier.map((siteRoleOption: SiteRoleOption) => {
        return {
          value: siteRoleOption.role,
          content: this.getSiteRoleOptionDisplayValue(siteRoleOption),
          disabled: this.checkSiteRoleDisabled(siteRoleOption)
        };
    });
  }

  private getSiteRoleOptionDisplayValue(siteRoleOption: SiteRoleOption) {
    const siteRoleDisplayValue = TypeUnsafeTranslation.translate(siteRoleOption.locKey);
    if (siteRoleOption.licenseTierRole === UnlicensedTier) {
      return siteRoleDisplayValue;
    }
    return SiteRoleIndentation + siteRoleDisplayValue;
  }

  private checkSiteRoleDisabled(siteRoleOption: SiteRoleOption){
      if(siteRoleOption.disabled){
        return true;
      }
      if(this.props.disabledRoleOptions){
        for (let i = 0; i < this.props.disabledRoleOptions.length; i++) {
          if (this.props.disabledRoleOptions[i].licenseTierRole === siteRoleOption.licenseTierRole &&
              this.props.disabledRoleOptions[i].locKey === siteRoleOption.locKey &&
              this.props.disabledRoleOptions[i].role === siteRoleOption.role) {
              return true;
          }
        }
      }
      return false;
  }

  private getButtonContent(): string{
    const siteRoleOption = this.props.getSiteRoleOptionBySiteRole(this.state.currentSiteRoleSelection);
    if(siteRoleOption !== undefined && siteRoleOption.role === SiteRolesResource.Mixed){
        return TypeUnsafeTranslation.translate(siteRoleOption.locKey);
    }
    return this.state.currentSiteRoleSelection;
  }
}

export namespace SiteRolePickerMenu {

  export interface Props extends Omit<SingleSelectDropdownProps, 'list' | 'type' | 'label'> {
    getSiteRoleOptionBySiteRole: (siteRole: string) => SiteRoleOption;
    onOptionSelected: (siteRoleOption: SiteRoleOption) => void;
    selectedRole: SiteRoleOption;
    siteRoleOptions: SiteRoleOption[];
    disabledRoleOptions?: SiteRoleOption[];
    dropdownLabel?: string;
    isDisabled?: boolean;
    panelStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    showSiteRoleInfoLink?: boolean;
    includeAnySiteRoleOption?: boolean;
  }

}
